import React from 'react';
import { TextField, Box, InputLabel } from '@mui/material';
import { useFormContext, Controller, useWatch } from 'react-hook-form';

import type { FieldError } from 'react-hook-form';
import type { CampaignQuestion } from '../../types';

interface QuestionTitleFieldProps {
    question: CampaignQuestion;
    error?: FieldError;
    fieldIndex?: number;
}

const MAX_TITLE_LENGTH = 120;

/**
 * Text input field for the question title.
 */
const QuestionTitleField: React.FC<QuestionTitleFieldProps> = ({ question, error, fieldIndex }) => {
    const inputId = `question-title-${question.id}`;
    const formContext = useFormContext();

    // Watch the current title value from the form state
    const titleValue =
        useWatch({
            control: formContext.control,
            name: `questions.${fieldIndex}.title` as const,
            defaultValue: '',
        }) || '';

    return (
        <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <InputLabel htmlFor={inputId} error={!!error}>
                    Title (optional)
                </InputLabel>
                <Box
                    component="span"
                    sx={{
                        fontSize: '0.75rem',
                        color: titleValue.length >= MAX_TITLE_LENGTH ? 'error.main' : 'text.secondary',
                    }}
                >
                    {titleValue.length}/{MAX_TITLE_LENGTH}
                </Box>
            </Box>
            <Controller
                name={`questions.${fieldIndex}.title` as const}
                control={formContext.control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        id={inputId}
                        size="small"
                        variant="outlined"
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                    />
                )}
            />
        </Box>
    );
};

export default React.memo(QuestionTitleField);
